import { useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { Screen } from "./app-frame.js";
import "./app.css";
import { BrandArea } from "./brand-area.js";
import { AppSidebar } from "./components/app-sidebar.js";
import { IntroHold, IntroProvider, ScreenLoader } from "./components/loader/index.js";
import { PaletteProvider } from "./components/palette/palette-context.js";
import { useToast } from "./components/toast.js";
import { TopBar } from "./components/top-bar.js";
import { TourContext, useTourController } from "./components/tour/tour.js";
import { WhatsNewProvider } from "./components/whats-new/whats-new.js";
import { type Me, signOut } from "./lib/api/index.js";
import { useChosenReport } from "./lib/chosen-report.js";
import {
  type Membership,
  useChosenWorkspace,
  WorkspaceContext,
  WorkspaceSwitchContext,
} from "./lib/chosen-workspace.js";
import {
  useBrands,
  useMay,
  useMe,
  useReports,
  useSubscription,
} from "./lib/queries/index.js";
import {
  documentTitle,
  PATHS,
  reportScreenPath,
  SCREEN_NAMES,
  usePageTitle,
  useScreenKind,
} from "./lib/routes.js";
import { SIGN_IN, signInFor } from "./lib/sign-in-path.js";
import { navigate, withReport } from "./lib/url-filters.js";
import { endScreenUsage, useScreenUsage } from "./lib/usage/use-screen-usage.js";
import { NoBrandsYet } from "./screens/onboarding/no-brands-yet.js";
import { WorkspaceClosed } from "./screens/workspace-closed.js";

/*
 * Loaded when first needed, not on every visit. Setup is seen once per brand
 * and the tour once per person, yet both sat in the first download and pushed
 * it past its 200 KB budget (frontend.md; `pnpm --filter @crux/web budget`).
 */
const OnboardingScreen = lazy(() =>
  import("./screens/onboarding/onboarding-screen.js").then((m) => ({
    default: m.OnboardingScreen,
  })),
);
const TourLayer = lazy(() =>
  import("./components/tour/tour-layer.js").then((m) => ({ default: m.TourLayer })),
);

/**
 * The signed-in half of the app: the session check, setup, and the rail.
 *
 * A route in `router.tsx`, wrapping every screen that needs an account. The
 * public pages — `/about` and the login callback — are its siblings rather
 * than its children, so they never ask about a session: `/about` is where our
 * crawler's User-Agent points, and a stranger reading it has no account.
 */
export function SignedInLayout() {
  // The intro sits above everything signed-in, so it outlives the session check
  // and the workspace load and lands in whichever logo the page ends up drawing.
  return (
    <IntroProvider>
      <SignedInBody />
    </IntroProvider>
  );
}

function SignedInBody() {
  const client = useQueryClient();
  const toast = useToast();
  const me = useMe(true);

  // Four states, not three (§15a). "Still checking" is not "signed out", and
  // bouncing someone to a login screen while the check is in flight would log
  // them out every time the network was slow.
  // The intro says it while the session is checked: the mark assembling on a
  // blank page, which is where every arrival starts.
  if (me.isPending) {
    return <IntroHold kind="arrival" />;
  }

  if (me.isError) {
    return (
      <div className="page">
        <div className="container narrow">
          <div className="notice notice-error">
            Could not check your session: {me.error.message}. You have not been signed out
            — we simply cannot tell right now.
          </div>
        </div>
      </div>
    );
  }

  if (me.data === null) {
    return <ToSignIn />;
  }

  return (
    <Chosen
      me={me.data}
      onSignOut={() => {
        // The screen on show is recorded before the session ends, or the
        // server would refuse it; the sign-out goes ahead whatever came of it.
        void endScreenUsage().finally(() =>
          signOut()
            .then(() => {
              // To the sign-in page first, with no `next`: whoever signs in next
              // should start at their own Overview, not at the screen this person
              // left. Then drop everything cached; it belongs to the session that
              // just ended.
              navigate(SIGN_IN, { replace: true });
              client.clear();
              toast.done("Signed out");
            })
            .catch((error: unknown) => {
              // Said, because a sign-out that silently did nothing leaves a
              // person believing a shared computer is safe to walk away from.
              toast.failed("Could not sign out. You are still signed in.", error);
            }),
        );
      }}
    />
  );
}

/**
 * A signed-out visit to a screen: off to sign in, remembering the screen.
 *
 * `replace`, so Back does not return to a page that would only send you here
 * again.
 */
function ToSignIn() {
  const from = `${window.location.pathname}${window.location.search}`;
  useEffect(() => {
    navigate(signInFor(from), { replace: true });
  }, [from]);
  return null;
}

/**
 * The workspace this browser is showing, of the ones this account is in.
 *
 * Its own component so the choice is a hook that runs only once somebody is
 * signed in. Switching opens the new workspace's Overview: a report id in the
 * address belongs to the workspace being left.
 */
function Chosen({ me, onSignOut }: { readonly me: Me; readonly onSignOut: () => void }) {
  const { current, choose } = useChosenWorkspace(me.user.id, me.workspaces);
  return (
    <Signed
      email={me.user.email}
      userName={me.user.name}
      userId={me.user.id}
      workspace={current}
      workspaces={me.workspaces}
      onChooseWorkspace={(workspaceId) => {
        choose(workspaceId);
        navigate(PATHS.overview);
      }}
      onSignOut={onSignOut}
    />
  );
}

/**
 * The signed-in shell.
 *
 * Split out because the brand list is server state and needs a hook, and a hook
 * cannot sit behind the early returns above it. Passing the workspace in keeps
 * the rule that a screen never picks its own tenant.
 */
function Signed({
  email,
  userName,
  userId,
  workspace,
  workspaces,
  onChooseWorkspace,
  onSignOut,
}: {
  readonly email: string;
  readonly userName: string;
  readonly userId: string;
  readonly workspace: Membership | null;
  readonly workspaces: readonly Membership[];
  readonly onChooseWorkspace: (workspaceId: string) => void;
  readonly onSignOut: () => void;
}) {
  const workspaceId = workspace?.workspaceId ?? null;
  const brands = useBrands(workspaceId ?? "", workspaceId !== null);
  const reports = useReports(workspaceId ?? "", workspaceId !== null);
  const measured = reports.data ?? [];
  const screen = useScreenKind();
  // Computed here rather than in `BrandArea` because the rail needs it too —
  // it is what the rail highlights, and what its links carry.
  const reportId = useChosenReport(measured, reportScreenPath(screen));
  // Here because the tour walks across screens and the rail restarts it.
  const tour = useTourController(reportId);

  // Setup is finished when a report is active — not when a report exists, and
  // not when it has questions. Setup activates it once its questions are saved,
  // so the wizard is not torn away mid-way; a report that later pauses every
  // question stays active and keeps its history.
  const measuring = measured.length > 0;
  const ready = brands.isSuccess && reports.isSuccess;
  const mayAddBrands = useMay(workspaceId, "addBrands");
  // A workspace whose plan ended "closed" opens only its plan (DESIGN.md,
  // "Access and plans"); the server refuses everything else anyway.
  const locked = useSubscription(workspaceId ?? "").data?.standing === "locked";
  // A workspace is arriving while its lists load — on a reload, and again on a
  // switch to another workspace — so the intro plays until they are in.
  const arriving =
    workspaceId !== null &&
    (brands.isPending || reports.isPending || mayAddBrands === "unknown") ? (
      <IntroHold kind="arrival" />
    ) : null;

  /*
   * Onboarding owns the whole viewport, with no app navigation above it.
   *
   * The masthead offers Overview, Prompts and Citations — screens that are
   * empty until this wizard finishes. Showing them during setup is an
   * invitation to leave and find nothing, which is the shape of the problem
   * this screen exists to solve.
   */
  /*
   * The wizard runs in two situations, and they are the same screen.
   *
   * Automatically, when the workspace has nothing to show — and deliberately,
   * when somebody asks for another brand from the rail. A second brand needs a
   * brand, its competitors and its questions exactly as much as the first did,
   * so it goes through the same three steps rather than through a shortened
   * form that would leave a report measuring nothing.
   */
  const asked = screen === "newBrand";
  // Your own account needs no brand, so setup never stands in front of it: a
  // person halfway through setup can still change their password.
  // Nor do the workspace's people or its AI apps: an owner halfway through
  // setup can still invite the colleague who knows the competitors.
  const ownAccount = screen === "profile" || screen === "members" || screen === "aiApps";
  const onboarding =
    !ownAccount &&
    !locked &&
    ((ready && workspaceId !== null && (brands.data.length === 0 || !measuring)) ||
      (asked && workspaceId !== null));

  // Here rather than in each screen, because this is the one place that knows
  // both which screen is showing and which brand it is about.
  const brandName = measured.find((report) => report.id === reportId)?.brand.name ?? null;
  // What is actually on screen: setup takes the whole window whichever
  // address it stands in front of.
  useScreenUsage(workspaceId, onboarding ? "setup" : screen);
  usePageTitle(
    onboarding
      ? documentTitle(SCREEN_NAMES.newBrand)
      : documentTitle(
          SCREEN_NAMES[screen],
          reportScreenPath(screen) === null ? null : brandName,
        ),
  );

  // Someone who may not add a brand is told who to ask instead (DESIGN.md,
  // "People and access"): the wizard would only be refused at its first step.
  if (onboarding && mayAddBrands === "no") {
    return (
      <NoBrandsYet
        workspaceName={workspace?.workspaceName ?? "this workspace"}
        asked={asked}
        onBack={() => {
          navigate(
            reportId === null ? PATHS.overview : withReport(PATHS.overview, reportId),
          );
        }}
        onSignOut={onSignOut}
      />
    );
  }

  // `onboarding` already requires a workspace; TypeScript follows the alias.
  if (onboarding) {
    return (
      <WorkspaceSwitchContext.Provider
        value={
          workspace !== null && workspaces.length > 1
            ? { current: workspace, workspaces, choose: onChooseWorkspace }
            : null
        }
      >
        {arriving}
        <Suspense
          fallback={
            <>
              <IntroHold kind="page" />
              <ScreenLoader caption="Loading setup" />
            </>
          }
        >
          <OnboardingScreen
            workspaceId={workspaceId}
            userName={userName}
            onSignOut={onSignOut}
            offerInvites={!asked && workspace?.role === "owner"}
            onDone={(newReportId) => {
              /*
               * Land on the report that was just made, not on whichever one the
               * fallback picks. A second brand finished the wizard and then showed
               * the first brand's numbers, which reads as the whole thing having
               * been thrown away.
               *
               * After the lists are fresh, not alongside. The cached list still has
               * the new report in setup, so the report chooser could not find it,
               * fell back to the first active report and rewrote the address to it
               * before the refetch arrived.
               */
              void Promise.all([brands.refetch(), reports.refetch()]).then(() => {
                if (asked) navigate(withReport(PATHS.overview, newReportId));
              });
            }}
            {...(asked
              ? {
                  // Back to the brand the wizard was opened from, which the rail
                  // puts in the address.
                  onCancel: () => {
                    navigate(
                      reportId === null
                        ? PATHS.overview
                        : withReport(PATHS.overview, reportId),
                    );
                  },
                }
              : {})}
          />
        </Suspense>
      </WorkspaceSwitchContext.Provider>
    );
  }

  return (
    <TourContext.Provider value={tour}>
      <WhatsNewProvider
        userId={userId}
        mayManage={workspace?.role === "owner" || workspace?.role === "admin"}
      >
        {arriving}
        <PaletteProvider
          host={{
            userId,
            workspaceId: workspaceId ?? "",
            workspaces,
            reports: measured,
            reportId,
            onChooseWorkspace,
            onTakeTour: tour.start,
            onSignOut,
          }}
        >
          <div className="shell">
            <AppSidebar
              workspaceId={workspaceId ?? ""}
              workspaceName={workspace?.workspaceName ?? "No workspace"}
              workspaces={workspaces}
              onChooseWorkspace={onChooseWorkspace}
              userName={userName}
              email={email}
              reports={measured}
              reportId={reportId}
              onSignOut={onSignOut}
              onTakeTour={tour.start}
            />
            <main>
              <TopBar
                screen={screen}
                report={measured.find((report) => report.id === reportId) ?? null}
                workspaceId={workspaceId}
                today={new Date()}
              />
              <WorkspaceContext.Provider
                value={
                  workspace === null
                    ? null
                    : { workspaceId: workspace.workspaceId, role: workspace.role, userId }
                }
              >
                {ownAccount ? (
                  <Screen />
                ) : locked && workspaceId !== null ? (
                  <WorkspaceClosed />
                ) : (
                  <BrandArea
                    workspaceId={workspaceId}
                    brands={brands}
                    reportId={reportId}
                  />
                )}
              </WorkspaceContext.Provider>
            </main>
          </div>
        </PaletteProvider>
        {tour.at === null ? null : (
          <Suspense fallback={null}>
            <TourLayer tour={tour} />
          </Suspense>
        )}
      </WhatsNewProvider>
    </TourContext.Provider>
  );
}
